import DataObjectIcon from "@mui/icons-material/DataObject";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {EditorContent, useEditor, useEditorState} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {memo, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import type {ReactNode} from "react";
import {
    CONTENT_LANGUAGES,
    FORMATS,
    matchesQuery,
    pickerItemsFor,
    resolveVariable,
    VARIABLE_NODE_NAME,
    VariableDetailPanel,
    VariablePickerPopover,
    variablesFor,
    type PickerVariable,
    type ResolveContext,
    type VariableAttributes,
    type VariablePreview,
} from "../../common/newVariablePicker";
import {VariableBubbleMenu} from "../../common/newVariablePicker/chip";
import {
    insertVariable,
    openVariablePicker,
    selectedVariable,
    variableDocumentText,
    variableEditorSx,
    variableExtensions,
} from "../../common/newVariablePicker/editor";
import {variableChipSx} from "../../common/variables/variableChipStyles";
import {printoutCatalogue, printoutSampleContext} from "./printoutLabCatalogue";

const NO_CHIP: VariableAttributes = {fallback: "", format: ""};

// The empty paragraphs and the hard break are what the Output panel must keep as blank lines and
// line breaks; the long date chip shows a format surviving the round trip.
const DOCUMENT_CONTENT = `
<p>Hallo <span data-variable="guestFirstName" data-fallback="Guest"></span>,</p>
<p></p>
<p>Ihre Buchung beginnt am <span data-variable="resStartDay"></span> um
<span data-variable="resStartTime"></span>. Ihr Konto steht bei
<span data-variable="folioTotal"></span>.</p>
<p>Anreise: <span data-variable="resStartDay" data-format="long"></span><br>Bis bald im
<span data-variable="hotelName"></span>.</p>
<p></p>
`;

type OutputMode = "resolved" | "tokens";

const LONG_TEXT = "Panoramasuite mit Bergblick, Balkon, Kamin und separatem Wohnbereich";

const TEN_NAMES = [
    "Anna", "Ben", "Clara", "David", "Eva",
    "Felix", "Greta", "Hannes", "Ida", "Jonas",
];

/** A day string off the local clock: `toISOString` would name yesterday east of Greenwich. */
const day = (offset: number) => {
    const date = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");

    date.setDate(date.getDate() + offset);

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

interface Scenario {
    label: string;
    variable: PickerVariable;
    context: ResolveContext;
}

/** The value a chip would show for a raw value the catalogue's own data may never produce. */
const given = (variable: PickerVariable, label: string, raw: unknown): Scenario => ({
    label,
    variable: {...variable, resolve: () => raw},
    context: {},
});

const EDGE_CASES: Record<string, (variable: PickerVariable) => Scenario[]> = {
    text: variable => [
        given(variable, "empty", ""),
        given(variable, "long", LONG_TEXT),
    ],
    localizedText: variable => [
        given(variable, "this language", {de: "Bergsuite", en: "Mountain suite", it: "Suite montagna"}),
        given(variable, "another language only", {fr: "Suite montagne"}),
    ],
    number: variable => [
        given(variable, "zero", 0),
        given(variable, "negative", -4),
        given(variable, "decimals", 1234.56),
    ],
    currency: variable => [
        given(variable, "zero", 0),
        given(variable, "large", 1234567.89),
        given(variable, "credit", -250.5),
        {
            label: "no currency code",
            variable: {...variable, resolve: () => 129, currency: () => undefined},
            context: {},
        },
    ],
    date: variable => [
        given(variable, "today", day(0)),
        given(variable, "past", "2019-01-07"),
        given(variable, "far future", "2099-12-31"),
        given(variable, "invalid", "not a date"),
    ],
    time: variable => [
        given(variable, "midnight", "00:00"),
        given(variable, "noon", "12:00"),
    ],
    duration: variable => [
        given(variable, "0 min", 0),
        given(variable, "45 min", 45),
        given(variable, "90 min", 90),
        given(variable, "600 min", 600),
    ],
    boolean: variable => [
        given(variable, "true", true),
        given(variable, "false", false),
        given(variable, "missing", undefined),
    ],
    list: variable => [
        given(variable, "one", ["Anna"]),
        given(variable, "empty", []),
        given(variable, "ten", TEN_NAMES),
    ],
};

const scenariosFor = (variable: PickerVariable, sample: ResolveContext): Scenario[] => [
    {label: "sample", variable, context: sample},
    {label: "no data", variable, context: {}},
    ...(EDGE_CASES[variable.kind]?.(variable) ?? []),
];

const KeyChip = ({children}: {children: string}) => (
    <Box component="span" sx={{...variableChipSx, whiteSpace: "nowrap"}}>{children}</Box>
);

const Cell = ({children, muted}: {children: ReactNode; muted?: boolean}) => (
    <Box
        component="td"
        sx={{
            py: 0.25,
            pr: 1.5,
            verticalAlign: "top",
            color: muted ? "text.disabled" : "text.primary",
            fontStyle: muted ? "italic" : "normal",
        }}
    >
        {children}
    </Box>
);

/**
 * One variable, its scenarios down the side and its kind's formats across the top.
 *
 * Memoized: the page re-renders on every keystroke in the editor, and ~150 cards of tables would
 * make typing lag. Its props only change with the language or the filter.
 */
const VariableCard = memo(function VariableCard({variable, sample, language}: {
    variable: PickerVariable;
    sample: ResolveContext;
    language: string;
}) {
    const formats = FORMATS[variable.kind] ?? [];
    const columns = formats.length > 0 ? formats : [""];
    const scenarios = useMemo(() => scenariosFor(variable, sample), [variable, sample]);
    const Icon = variable.icon;

    return (
        <Paper variant="outlined" sx={{borderRadius: 2, p: 2, width: 420, flexShrink: 0}}>
            <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {!!Icon && (
                        <Box sx={{display: "inline-flex", color: "text.secondary"}}>
                            <Icon fontSize="small"/>
                        </Box>
                    )}
                    <KeyChip>{variable.key}</KeyChip>
                    <Typography variant="body2" sx={{fontWeight: 600}}>{variable.label}</Typography>
                    <Box sx={{flex: 1}}/>
                    <Typography variant="body2" color="text.disabled">{variable.kind}</Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">{variable.description}</Typography>

                <Typography variant="body2" color="text.disabled" sx={{fontFamily: "monospace", wordBreak: "break-all"}}>
                    {variable.path ?? `Computed: ${variable.compute ?? "—"}`}
                </Typography>

                <Box component="table" sx={{width: "100%", borderCollapse: "collapse", fontSize: 12, mt: 0.5}}>
                    <Box component="thead">
                        <Box component="tr">
                            <Cell muted>scenario</Cell>
                            {columns.map(format => (
                                <Cell key={format || "value"} muted>{format || "value"}</Cell>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {scenarios.map(scenario => (
                            <Box
                                component="tr"
                                key={scenario.label}
                                sx={{"&:nth-of-type(odd)": {bgcolor: theme => alpha(theme.palette.text.primary, 0.02)}}}
                            >
                                <Cell muted>{scenario.label}</Cell>
                                {columns.map(format => {
                                    const value = resolveVariable(scenario.variable, {
                                        context: scenario.context,
                                        language,
                                        format,
                                    });

                                    return (
                                        <Cell key={format || "value"} muted={!value}>
                                            {value || variable.fallback || "—"}
                                        </Cell>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
});

const LabPanel = ({title, action, children}: {title: string; action?: ReactNode; children: ReactNode}) => (
    <Paper variant="outlined" sx={{borderRadius: 2, overflow: "hidden"}}>
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={theme => ({
                minHeight: 48,
                px: 2,
                py: 0.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
            })}
        >
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            {action}
        </Stack>
        {children}
    </Paper>
);

const NewVariablePickerTestPage = () => {
    const {t} = useTranslation();
    const [language, setLanguage] = useState(CONTENT_LANGUAGES[0].code);
    const [query, setQuery] = useState("");
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    const [outputMode, setOutputMode] = useState<OutputMode>("resolved");

    // Catalogue strings may contain colons; never treat them as i18next namespaces.
    const variables = useMemo(
        () => variablesFor(printoutCatalogue(), text => t(text, {nsSeparator: false})),
        [t],
    );
    const sample = useMemo(() => printoutSampleContext(language), [language]);
    const preview = useMemo<VariablePreview>(() => ({language, context: sample}), [language, sample]);
    const pickerItems = useMemo(() => pickerItemsFor(variables, preview), [variables, preview]);

    /*
        Built once with the first catalogue; later ones reach it through the store (effect below).
        Held in state: a fresh array each render fails useEditor's identity check, and it would call
        `setOptions` — a full view update — on every keystroke.
     */
    const [extensions] = useState(() => [
        StarterKit,
        ...variableExtensions({variables, preview}, {search: false}),
    ]);
    const editor = useEditor({extensions, content: DOCUMENT_CONTENT});

    // Swapping the catalogue repaints the chips and the "/" examples without rebuilding the editor.
    useEffect(() => {
        editor.commands.setVariableCatalogue({variables, preview});
    }, [editor, variables, preview]);

    const selected = useEditorState({
        editor,
        selector: ({editor: instance}) => selectedVariable(instance),
    });

    const doc = useEditorState({
        editor,
        selector: ({editor: instance}) => instance.state.doc,
    });

    // The reader text also depends on the catalogue, so a language switch re-resolves it.
    const output = useMemo(
        () => variableDocumentText(
            doc,
            outputMode === "tokens"
                ? {kind: "tokens"}
                : {kind: "resolved", catalogue: {variables, preview}},
        ),
        [doc, outputMode, variables, preview],
    );

    const update = (attributes: Partial<VariableAttributes>) => {
        const at = editor.state.selection.from;

        // Re-asserting the selection keeps the panel open while the Default field is being typed in.
        editor.chain().updateAttributes(VARIABLE_NODE_NAME, attributes).setNodeSelection(at).run();
    };

    const shown = variables.filter(variable => matchesQuery(variable, query));
    const scopes = shown.reduce<{scope: string; variables: PickerVariable[]}[]>((groups, variable) => {
        const group = groups[groups.length - 1];

        if (group?.scope === variable.scope) group.variables.push(variable);
        else groups.push({scope: variable.scope, variables: [variable]});

        return groups;
    }, []);

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: 0,
                overflowY: "auto",
                bgcolor: "background.default",
                px: {xs: 2, md: 4},
                py: {xs: 3, md: 4},
            }}
        >
            <Stack gap={3}>
                <Box>
                    <Typography variant="h5">New variable picker</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, maxWidth: 900}}>
                        Fully mocked. The picker is{" "}
                        <Box component="code">common/newVariablePicker</Box>; the catalogue and the
                        sample booking are the printouts', adapted in this lab. Type{" "}
                        <Box component="code">/</Box> at the start of a word to open it, arrows move,
                        Enter or Tab inserts, Esc closes the info card first and the popup second. The
                        list is virtualized, so the whole catalogue scrolls without lag; Popover opens
                        the same panel from a toolbar button, with its own search field. Output shows
                        the document as a reader sees it, or as it is stored, blank lines and line
                        breaks included. Every card below is one variable, resolved by the core for
                        the language chosen here — the rows are scenarios, the columns its kind's
                        formats.
                    </Typography>
                </Box>

                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={language}
                        onChange={(_event, next) => next && setLanguage(next)}
                    >
                        {CONTENT_LANGUAGES.map(option => (
                            <ToggleButton key={option.code} value={option.code} sx={{textTransform: "none", px: 2}}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <TextField
                        size="small"
                        placeholder="Filter by key, label or scope"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{width: 320}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small"/>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Typography variant="body2" color="text.secondary">
                        {shown.length} of {variables.length} variables
                    </Typography>
                </Stack>

                <Stack direction={{xs: "column", lg: "row"}} gap={3} alignItems="flex-start">
                    <Stack gap={3} sx={{flex: 1, minWidth: 0, maxWidth: 820}}>
                        <LabPanel
                            title="Document"
                            action={
                                <Stack direction="row" gap={1}>
                                    <Button
                                        size="small"
                                        startIcon={<DataObjectIcon/>}
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={() => openVariablePicker(editor)}
                                    >
                                        Variable
                                    </Button>
                                    <Button
                                        size="small"
                                        startIcon={<ManageSearchOutlinedIcon/>}
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={event => setPopoverAnchor(event.currentTarget)}
                                    >
                                        Popover
                                    </Button>
                                </Stack>
                            }
                        >
                            <Box
                                onMouseDown={event => {
                                    const target = event.target as Node;

                                    // The bubble menu is a portal: its React events have no DOM ancestor here.
                                    if (!event.currentTarget.contains(target)) return;
                                    if (editor.view.dom.contains(target)) return;

                                    event.preventDefault();
                                    editor.chain().focus("end").run();
                                }}
                                sx={theme => ({
                                    p: 3,
                                    minHeight: 180,
                                    cursor: "text",
                                    typography: "body1",
                                    lineHeight: 1.7,
                                    "& .tiptap": {outline: "none"},
                                    "& .tiptap p": {margin: 0, marginBottom: theme.spacing(2)},
                                    "& .tiptap p:last-child": {marginBottom: 0},
                                    ...variableEditorSx(theme),
                                })}
                            >
                                <EditorContent editor={editor}/>
                                <VariableBubbleMenu editor={editor}/>
                            </Box>
                        </LabPanel>

                        <LabPanel
                            title="Output"
                            action={
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={outputMode}
                                    onChange={(_event, next: OutputMode | null) => next && setOutputMode(next)}
                                >
                                    <ToggleButton value="resolved" sx={{textTransform: "none", px: 1.5, py: 0.25}}>
                                        Reader
                                    </ToggleButton>
                                    <ToggleButton value="tokens" sx={{textTransform: "none", px: 1.5, py: 0.25}}>
                                        Stored text
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            }
                        >
                            <Box
                                sx={{
                                    p: 3,
                                    typography: "body2",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontFamily: outputMode === "tokens" ? "monospace" : undefined,
                                }}
                            >
                                {output}
                            </Box>
                        </LabPanel>
                    </Stack>

                    <Box sx={{width: {xs: "100%", lg: 360}, flexShrink: 0}}>
                        <LabPanel title="Variable">
                            <Box sx={{p: 3}}>
                                <VariableDetailPanel
                                    variable={selected
                                        ? variables.find(entry => entry.key === selected.key) ?? null
                                        : null}
                                    value={selected ?? NO_CHIP}
                                    onChange={update}
                                    preview={preview}
                                />
                            </Box>
                        </LabPanel>
                    </Box>
                </Stack>

                {scopes.map(group => (
                    <Stack key={group.scope} gap={1.5}>
                        <Typography variant="overline" color="text.secondary">
                            {group.scope} · {group.variables.length}
                        </Typography>
                        <Stack direction="row" gap={2} flexWrap="wrap" useFlexGap>
                            {group.variables.map(variable => (
                                <VariableCard
                                    key={variable.key}
                                    variable={variable}
                                    sample={sample}
                                    language={language}
                                />
                            ))}
                        </Stack>
                    </Stack>
                ))}

                {shown.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No variable matches “{query.trim()}”.
                    </Typography>
                )}
            </Stack>

            <VariablePickerPopover
                anchorEl={popoverAnchor}
                open={!!popoverAnchor}
                items={pickerItems}
                onSelect={item => {
                    insertVariable(editor, item.definition);
                    setPopoverAnchor(null);
                }}
                onClose={() => setPopoverAnchor(null)}
            />
        </Box>
    );
};

export default NewVariablePickerTestPage;
